import { useRef, useState } from 'react';
import { api } from '../../api/client';
import type { MessageDemande } from '../../types';
import { BoutonCopier } from './BoutonCopier';
import { EtatMessage } from './EtatMessage';
import { attendUneAction, estLigneSansTelephone, nomsSansTelephone, quand } from './format';
import { annoncerMiseAJourMessages } from './useCompteMessages';

/**
 * Les SMS d'annulation du jour : numéros et texte à copier, personnes sans numéro à appeler, puis « Marquer comme envoyés »
 * (docs/DECISIONS.md §24). Aucun fournisseur SMS n'est branché : le secrétariat les envoie tout de suite, depuis ici.
 */
export function MessagesSms({ demandeId, messages, titre, onChange }: {
  demandeId: number; messages: MessageDemande[]; titre: string; onChange: () => void;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [annonce, setAnnonce] = useState('');
  const bloc = useRef<HTMLDivElement>(null);
  if (messages.length === 0) return null;

  const sms = messages.filter((m) => !estLigneSansTelephone(m.destinataire));
  const ligneAAppeler = messages.find((m) => estLigneSansTelephone(m.destinataire)) ?? null;
  const aAppeler = nomsSansTelephone(ligneAAppeler?.destinataire);
  const enAttente = messages.filter(attendUneAction);
  // Les SMS d'un côté, les appels de l'autre : « Marquer comme envoyés » ne dit rien des personnes sans numéro.
  const smsEnAttente = sms.filter(attendUneAction);
  const peutMarquer = smsEnAttente.some((m) => m.peut_marquer);
  const appelsEnAttente = ligneAAppeler != null && attendUneAction(ligneAAppeler) && ligneAAppeler.peut_marquer;
  const texte = (sms[0] ?? messages[0]).corps;
  const numeros = sms.map((m) => m.destinataire).filter((n): n is string => !!n);
  const fait = messages.find((m) => m.statut === 'envoye');
  const toutPlusUtile = messages.every((m) => m.statut === 'plus_utile');

  async function marquerAppels() {
    if (!ligneAAppeler) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.marquerMessageEnvoye(ligneAAppeler.id);
      setAnnonce('Personnes sans numéro marquées comme appelées.');
      annoncerMiseAJourMessages();
      onChange();
      bloc.current?.focus();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Réessayez.");
      onChange();
    } finally {
      setEnCours(false);
    }
  }

  async function marquer() {
    setEnCours(true);
    setErreur(null);
    try {
      await api.marquerSmsEnvoyes(demandeId);
      setAnnonce('SMS marqués comme envoyés.');
      annoncerMiseAJourMessages();
      onChange();
      bloc.current?.focus();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Réessayez.");
      onChange();
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="msg" ref={bloc} tabIndex={-1} data-etat={enAttente.length > 0 ? 'a_envoyer' : toutPlusUtile ? 'plus_utile' : 'envoye'}>
      <div className="msg-haut">
        <span className="msg-canal">SMS</span>
        <b>{titre}</b>
        {enAttente.length > 0
          ? <EtatMessage statut="a_envoyer" libelle="À envoyer maintenant" />
          : <EtatMessage statut={toutPlusUtile ? 'plus_utile' : 'envoye'} />}
      </div>
      {enAttente.length === 0 && fait && (
        <div className="msg-meta">Envoyés{fait.envoye_par_vous ? ' par vous' : fait.envoye_par_nom ? ` par ${fait.envoye_par_nom}` : ''}, le {quand(fait.envoye_at, false)}</div>
      )}
      {toutPlusUtile && <div className="msg-meta">{messages[0].plus_utile_motif ?? "Ces SMS ne sont plus utiles."}</div>}
      {!toutPlusUtile && (
        <ul className="msg-liste">
          <li className="msg-ligne">
            <span className="msg-ligne-qui">{numeros.length} participant·e·s avec un numéro</span>
            {numeros.length > 0 && <BoutonCopier texte={numeros.join(', ')} libelle="Copier les numéros" etiquette="Copier les numéros de téléphone des participant·e·s" />}
          </li>
          <li className="msg-ligne">
            <span className="msg-ligne-qui">Le texte du SMS</span>
            <BoutonCopier texte={texte} libelle="Copier le texte" etiquette="Copier le texte du SMS" />
          </li>
          {aAppeler.length > 0 && (
            <li className="msg-ligne">
              <span className="msg-ligne-qui">{appelsEnAttente ? 'À appeler' : 'Appelé·e·s'} : {aAppeler.join(', ')}</span>
              <span className="msg-meta">pas de numéro</span>
              {appelsEnAttente && (
                <button type="button" className="btn btn-sm" disabled={enCours} onClick={marquerAppels} aria-label={`Marquer comme appelé·e·s : ${aAppeler.join(', ')}`}>
                  Marquer comme appelé·e·s
                </button>
              )}
            </li>
          )}
        </ul>
      )}
      {smsEnAttente.length > 0 && peutMarquer && (
        <div className="msg-actions">
          <button type="button" className="btn btn-sm btn-accent" disabled={enCours} onClick={marquer} aria-label={`Marquer comme envoyés : ${titre}`}>
            {enCours ? <span className="spinner spinner-sm" role="status" aria-label="Enregistrement en cours" /> : 'Marquer comme envoyés'}
          </button>
        </div>
      )}
      <span className="sr-only" role="status">{annonce}</span>
      {erreur && <div className="text-xs text-err" role="alert">{erreur}</div>}
    </div>
  );
}
